import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { APP_VERSION } from '../../../shared/app-version.js';

const QUERY_CACHE_STORAGE_PREFIX = 'anysync:query-cache';
const QUERY_CACHE_SCHEMA_VERSION = 1;
const QUERY_CACHE_PERSIST_DEBOUNCE_MS = 250;

type QueryCacheStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

type QueryCachePersistenceEnv = {
  VITE_QUERY_CACHE_PERSISTENCE_DISABLED?: string;
};

type PersistedQuery = {
  queryKey: QueryKey;
  data: unknown;
  dataUpdatedAt: number;
};

export type PersistedQueryCachePayload = {
  schemaVersion: typeof QUERY_CACHE_SCHEMA_VERSION;
  appVersion: string;
  userId: string;
  savedAt: number;
  queries: PersistedQuery[];
};

type PersistableQueryLike = {
  queryKey: QueryKey;
  state: {
    data: unknown;
    dataUpdatedAt: number;
    status: string;
  };
};

type QueryCachePersistenceOptions = {
  appVersion?: string;
  env?: QueryCachePersistenceEnv;
  now?: () => number;
  storage?: QueryCacheStorage | null;
};

export type QueryCachePersistenceController = {
  flush: () => boolean;
  restored: boolean;
  storageKey: string;
  teardown: () => void;
};

let activePersistence: QueryCachePersistenceController | null = null;

function isTruthyFlag(value: string | undefined): boolean {
  const normalized = value?.trim().toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on';
}

export function isQueryCachePersistenceDisabled(
  env: QueryCachePersistenceEnv = import.meta.env,
): boolean {
  return isTruthyFlag(env.VITE_QUERY_CACHE_PERSISTENCE_DISABLED);
}

export function buildQueryCachePersistenceStorageKey({
  appVersion = APP_VERSION,
  userId,
}: {
  appVersion?: string;
  userId: string;
}): string {
  return `${QUERY_CACHE_STORAGE_PREFIX}:v${appVersion}:user:${userId}`;
}

function browserStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function isPersistableQueryKey(queryKey: QueryKey): boolean {
  if (!Array.isArray(queryKey)) {
    return false;
  }

  if (queryKey.length === 1 && queryKey[0] === 'projects') {
    return true;
  }

  if (queryKey.length === 2 && queryKey[0] === 'projects' && queryKey[1] === 'listing-stats') {
    return true;
  }

  if (
    queryKey.length === 3 &&
    queryKey[0] === 'projects' &&
    typeof queryKey[1] === 'string' &&
    queryKey[2] === 'collections'
  ) {
    return true;
  }

  if (
    queryKey.length === 5 &&
    queryKey[0] === 'projects' &&
    typeof queryKey[1] === 'string' &&
    queryKey[2] === 'collections' &&
    typeof queryKey[3] === 'string' &&
    (queryKey[4] === 'fields' || queryKey[4] === 'field-usage')
  ) {
    return true;
  }

  return false;
}

function isPersistableQuery(query: PersistableQueryLike): boolean {
  return (
    query.state.status === 'success' &&
    query.state.data !== undefined &&
    query.state.dataUpdatedAt > 0 &&
    isPersistableQueryKey(query.queryKey)
  );
}

export function buildPersistedQueryCachePayload(
  queryClient: QueryClient,
  userId: string,
  {
    appVersion = APP_VERSION,
    now = Date.now,
  }: {
    appVersion?: string;
    now?: () => number;
  } = {},
): PersistedQueryCachePayload {
  return {
    schemaVersion: QUERY_CACHE_SCHEMA_VERSION,
    appVersion,
    userId,
    savedAt: now(),
    queries: queryClient
      .getQueryCache()
      .findAll()
      .filter(isPersistableQuery)
      .map((query) => ({
        queryKey: query.queryKey,
        data: query.state.data,
        dataUpdatedAt: query.state.dataUpdatedAt,
      })),
  };
}

function parsePersistedPayload(raw: string | null): PersistedQueryCachePayload | null {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<PersistedQueryCachePayload>;
    if (
      parsed.schemaVersion !== QUERY_CACHE_SCHEMA_VERSION ||
      typeof parsed.appVersion !== 'string' ||
      typeof parsed.userId !== 'string' ||
      typeof parsed.savedAt !== 'number' ||
      !Array.isArray(parsed.queries)
    ) {
      return null;
    }

    return parsed as PersistedQueryCachePayload;
  } catch {
    return null;
  }
}

export function restorePersistedQueryCache(
  queryClient: QueryClient,
  userId: string,
  {
    appVersion = APP_VERSION,
    storage = browserStorage(),
  }: {
    appVersion?: string;
    storage?: QueryCacheStorage | null;
  } = {},
): boolean {
  if (!storage) {
    return false;
  }

  const storageKey = buildQueryCachePersistenceStorageKey({ appVersion, userId });
  const payload = parsePersistedPayload(storage.getItem(storageKey));
  if (!payload || payload.appVersion !== appVersion || payload.userId !== userId) {
    return false;
  }

  for (const query of payload.queries) {
    if (!isPersistableQueryKey(query.queryKey) || query.data === undefined) {
      continue;
    }

    queryClient.setQueryData(query.queryKey, query.data, {
      updatedAt: query.dataUpdatedAt || payload.savedAt,
    });
  }

  return true;
}

export function persistQueryCacheNow(
  queryClient: QueryClient,
  userId: string,
  {
    appVersion = APP_VERSION,
    now = Date.now,
    storage = browserStorage(),
  }: {
    appVersion?: string;
    now?: () => number;
    storage?: QueryCacheStorage | null;
  } = {},
): boolean {
  if (!storage) {
    return false;
  }

  const storageKey = buildQueryCachePersistenceStorageKey({ appVersion, userId });
  const payload = buildPersistedQueryCachePayload(queryClient, userId, {
    appVersion,
    now,
  });

  try {
    if (payload.queries.length === 0) {
      storage.removeItem(storageKey);
      return true;
    }

    storage.setItem(storageKey, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

export function teardownQueryCachePersistence() {
  activePersistence?.teardown();
  activePersistence = null;
}

export function configureQueryCachePersistence(
  queryClient: QueryClient,
  userId: string | null,
  options: QueryCachePersistenceOptions = {},
): QueryCachePersistenceController | null {
  if (!userId || isQueryCachePersistenceDisabled(options.env)) {
    teardownQueryCachePersistence();
    return null;
  }

  const storage = options.storage === undefined ? browserStorage() : options.storage;
  if (!storage) {
    teardownQueryCachePersistence();
    return null;
  }

  const appVersion = options.appVersion ?? APP_VERSION;
  const now = options.now ?? Date.now;
  const storageKey = buildQueryCachePersistenceStorageKey({ appVersion, userId });
  if (activePersistence?.storageKey === storageKey) {
    return activePersistence;
  }

  teardownQueryCachePersistence();

  const restored = restorePersistedQueryCache(queryClient, userId, {
    appVersion,
    storage,
  });

  let flushTimer: ReturnType<typeof setTimeout> | null = null;

  const flush = () => {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }

    return persistQueryCacheNow(queryClient, userId, {
      appVersion,
      now,
      storage,
    });
  };

  const scheduleFlush = () => {
    if (flushTimer) {
      return;
    }

    flushTimer = setTimeout(() => {
      void flush();
    }, QUERY_CACHE_PERSIST_DEBOUNCE_MS);
  };

  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    const query = 'query' in event ? event.query : null;
    if (query && isPersistableQueryKey(query.queryKey)) {
      scheduleFlush();
    }
  });

  const handlePageHide = () => {
    void flush();
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', handlePageHide);
  }

  activePersistence = {
    flush,
    restored,
    storageKey,
    teardown: () => {
      if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = null;
      }
      unsubscribe();
      if (typeof window !== 'undefined') {
        window.removeEventListener('pagehide', handlePageHide);
      }
    },
  };

  return activePersistence;
}
